import * as React from "react";
import {
  BreedGroupApi,
  BreedGroupDto,
  BreedGroupTranslationDto,
} from "../../../../api";
import { useTl, useApiService, useCrudApi } from "../../../../hooks";
import { useHistory } from "react-router";
import { ETLCodes } from "../../../../locales";
import { ERoutes } from "../../../../ERoutes";
import { SmallFormGenerator, FGMultiTranslate } from "../../../../components";
import { FieldGroup, FGNumberInput, FGTextAreaInput } from "nsitools-react";
import * as Yup from "yup";
import { useGlobalData } from "../../../../contexts";

export interface IBreedGroupDetailPageProps {
  idBreedGroup: number;
  editMode: boolean;
}

export const BreedGroupDetailPage: React.FunctionComponent<IBreedGroupDetailPageProps> = ({
  idBreedGroup,
  editMode,
}) => {
  const { t } = useTl();
  const api = useApiService(BreedGroupApi);
  const history = useHistory();
  const { languages } = useGlobalData();
  const {
    data,
    loading,
    deleteItem,
    deleting,
    saveItem,
    saving,
  } = useCrudApi<BreedGroupDto>(
    {
      getApiFn: (id = idBreedGroup) =>
        id > 0
          ? api.get(id)
          : new BreedGroupDto({
              breedGroupTranslations: [
                ...languages.map(
                  (l) =>
                    new BreedGroupTranslationDto({ languageId: l.languageId })
                ),
              ],
            }).toJSON(),
      saveApiFn: (d) => api.save(d),
      onSavedRoute: (d) =>
        `${ERoutes.breedGroups}/${d.breedGroupId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.breedGroupId),
      onDeletedRoute: () => ERoutes.breedGroups,
    },
    [idBreedGroup]
  );

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      breedGroupTranslations: Yup.array().of(
        Yup.object().shape({
          text: Yup.string().nullable().required(t(ETLCodes.Required)),
        })
      ),
      breedGroupNumber: Yup.number()
        .required(t(ETLCodes.Required))
        .test(
          "number-unique",
          t(ETLCodes.BreedGroupNumberAlreadyExists),
          async function (value) {
            if (!value) return true;
            const res = await api.isBreedGroupNumberUnique(idBreedGroup, value);
            return res.value;
          }
        ),
    });
  }, [api, idBreedGroup, t]);

  return (
    data && (
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.breedGroups)}
        onDelete={deleteItem}
        showDeleteButton={idBreedGroup > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup>
          <FGNumberInput
            name="breedGroupNumber"
            label={t(ETLCodes.BreedGroupNumber)}
          />
          <FGMultiTranslate
            label={ETLCodes.Name}
            translationsArrayField="breedGroupTranslations"
            translationField="text"
            translationDto={BreedGroupTranslationDto}
          />
          <FGTextAreaInput name="remark" label={t(ETLCodes.Remark)} />
        </FieldGroup>
      </SmallFormGenerator>
    )
  );
};
